import React, {
    useEffect, useState, useCallback, useContext
} from 'react';
import { withRouter } from 'react-router-dom';
import { Loader } from '@jutro/components';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { useAuthentication } from 'gw-digital-auth-react';
import _ from 'lodash';
import { TranslatorContext } from '@jutro/locale';
import { PolicyService } from 'gw-capability-policy';
import { ClaimService } from 'gw-capability-claim';
import {
    withApplicationPropertyContext
} from 'tdic-components-platform-react';
import SurveyQuestionParser from '../../components/SurveyQuestionParser/QuestionParserComponent';
import metadata from './PLRenewalSurvey.metadata.json5';
import styles from '../../Survey.module.scss';
import messages from '../../Survey.messages';
import PLQuestionConfig from '../../question-config/pl-renewal-question-config.json5';
import EPLQuestionConfig from '../../question-config/epl-renewal-question-config.json5';
import QuestionValidation from '../../util/ValidationUtil';

function PLRenewalSurvey({ history, tncTransactionTypesSubTypes, termData: terms }) {
    const translator = useContext(TranslatorContext);
    const { authHeader, user_name: userName } = useAuthentication();
    const auth = useAuthentication();
    const [isLoading, updateLoading] = useState(true);
    const [plAcknowledged, updatePLAcknowledged] = useState(false);
    const [eplAcknowledged, updateEPLAcknowledged] = useState(false);
    const [formSubmitted, updateFormSubmitted] = useState(false);
    const [renewalPolicy, updateRenewalPolicy] = useState({});
    const [formValid, updateFormValid] = useState(false);
    const [plFormValid, updatePLFormValid] = useState(false);
    const [eplFormValid, updateEPLFormValid] = useState(false);
    const [stateList, updateStateList] = useState([]);
    const [plQuestionSet,
        updatePLQuestionSet] = useState(_.cloneDeep(PLQuestionConfig.questionSet));
    const [eplQuestionSet,
        updateEPLQuestionSet] = useState(_.cloneDeep(EPLQuestionConfig.questionSet));

    useEffect(() => {
        ClaimService.getStatesList(authHeader).then((states) => updateStateList(states));
        PolicyService.retrievePoliciesForPolicyChangeSurvey(authHeader)
            .then((policies) => !_.isEmpty(policies) && updateRenewalPolicy(policies[0]))
            .finally(() => updateLoading(false));
    }, [auth.authHeader, authHeader]);

    const validateFields = useCallback(() => {
        const isPLValid = QuestionValidation.validate(plQuestionSet);
        updatePLFormValid(isPLValid);
        if (_.get(renewalPolicy, 'isEPLRenewalSurveyAvailable', false)) {
            const isEPLValid = QuestionValidation.validate(eplQuestionSet);
            updateEPLFormValid(isEPLValid);
            return updateFormValid(isPLValid && isEPLValid);
        }
        return updateFormValid(isPLValid);
    }, [eplQuestionSet, plQuestionSet, renewalPolicy]);

    const handlePLAgreeChange = useCallback((value) => {
        updatePLAcknowledged(value);
        validateFields();
    }, [validateFields]);
    const handleEPLAgreeChange = useCallback((value) => {
        updateEPLAcknowledged(value);
        validateFields();
    }, [validateFields]);

    const handlePLValueChange = useCallback((value, path, questionList) => {
        if (_.isArray(questionList) && !_.isEmpty(questionList)) {
            updatePLQuestionSet([...questionList]);
            return validateFields();
        }
        const questions = [...plQuestionSet];
        _.set(questions, `${path}.value`, value);
        updatePLQuestionSet([...questions]);
        return validateFields();
    }, [plQuestionSet, validateFields]);

    const handleEPLValueChange = useCallback((value, path, questionList) => {
        if (_.isArray(questionList) && !_.isEmpty(questionList)) {
            updateEPLQuestionSet([...questionList]);
            return validateFields();
        }
        const questions = [...eplQuestionSet];
        _.set(questions, `${path}.value`, value);
        updateEPLQuestionSet([...questions]);
        return validateFields();
    }, [eplQuestionSet, validateFields]);

    const getOptionsValue = useCallback((optionsList) => {
        let stateName;
        const options = _.map(optionsList, (item) => {
            if (item.isState) {
                const stp = stateList.find((st) => st.code === item.value);
                if (stp) {
                    stateName = stp.displayName;
                }
            }
            return {
                optionName: item.label,
                optionValue: item.isState ? stateName : item.value,
                inputType: item.type,
            };
        });
        return options;
    }, [stateList]);

    const questionsMap = useCallback((item) => {
        let answer = '';
        if (_.has(item, 'value')) {
            const ve = _.get(item, 'value');
            if (ve === true) {
                answer = 'Yes';
            } else if (ve === false) {
                answer = 'No';
            } else if (ve) {
                answer = ve;
            }
        }
        if (!_.isEmpty(item.options)) {
            return {
                question: item.question,
                answer: answer,
                inputType: item.type,
                options: getOptionsValue(item.options)
            };
        }
        return {
            question: item.question,
            answer: answer,
            inputType: item.type
        };
    }, [getOptionsValue]);

    const getPlQuestions = useCallback(() => {
        const questions = [];
        _.forEach(plQuestionSet, (item) => {
            questions.push(questionsMap(item));
        });
        return questions;
    }, [plQuestionSet, questionsMap]);

    const getEplQuestions = useCallback(() => {
        const questions = [];
        if (_.get(renewalPolicy, 'isEPLRenewalSurveyAvailable', false)) {
            _.forEach(eplQuestionSet, (item) => {
                questions.push(questionsMap(item));
            });
        }
        return questions;
    }, [eplQuestionSet, questionsMap, renewalPolicy]);

    const prepareRequest = useCallback(() => {
        return {
            policyNumber: _.get(renewalPolicy, 'policyNumber'),
            name: _.get(auth, 'name', ''),
            plQuestions: getPlQuestions(),
            EplQuestions: getEplQuestions()
        };
    }, [auth, getEplQuestions, getPlQuestions, renewalPolicy]);

    const handleSaveAcceptance = useCallback((tncType, tncSubTypes) => {
        const reqOption = {
            Version: _.get(terms, 'Version', ''),
            acceptance: 1,
            Transaction_Detail: _.get(renewalPolicy, 'policyNumber'),
            Transaction_Type: _.get(tncType, 'id'),
            Transaction_SubTypes: tncSubTypes,
            email_Address: userName
        };
        PolicyService
            .saveUserTermsAndConditions(reqOption, authHeader);
    }, [terms, renewalPolicy, userName, authHeader]);

    const handleSubmitClick = useCallback(() => {
        updateLoading(true);
        const request = prepareRequest();
        PolicyService.sendPolicyChangeSurvey(authHeader, request)
            .then((response) => {
                updateFormSubmitted(response);
                updateEPLQuestionSet(_.cloneDeep(EPLQuestionConfig.questionSet));
                updatePLQuestionSet(_.cloneDeep(PLQuestionConfig.questionSet));
                updateLoading(false);

                const tncType = _.find(tncTransactionTypesSubTypes, { transaction_Type: 'Survey' });
                let tncSubType = _.find(tncType.transactionSubTypes, { transaction_SubType: 'PolicyChangePLSurvey' });
                const subTypes = [tncSubType.id];
                if (_.get(renewalPolicy, 'isEPLRenewalSurveyAvailable', false)) {
                    tncSubType = _.find(tncType.transactionSubTypes, { transaction_SubType: 'PolicyChangeEPLSurvey' });
                    subTypes.push(tncSubType.id);
                }
                handleSaveAcceptance(tncType, subTypes);
            })
            .catch(() => updateLoading(false));
    }, [authHeader, handleSaveAcceptance, prepareRequest, renewalPolicy,
        tncTransactionTypesSubTypes]);

    const handleCloseClick = useCallback(() => {
        history.push('/home');
    }, [history]);

    const isFormValid = useCallback(() => {
        if (_.get(renewalPolicy, 'isEPLRenewalSurveyAvailable', false)) {
            return !plAcknowledged || !eplAcknowledged || !formValid;
        }
        return !plAcknowledged || !formValid;
    }, [eplAcknowledged, formValid, plAcknowledged, renewalPolicy]);

    const getConfirmationInfo = useCallback(() => {
        return _.isEmpty(renewalPolicy)
            ? translator(messages.confirmationSurvey)
            : translator(messages.confirmationSurveyWithPolicy, { policyNumber: _.get(renewalPolicy, 'policyNumber') });
    }, [renewalPolicy, translator]);

    const getPageTitleInfo = useCallback(() => {
        return _.isEmpty(renewalPolicy)
            ? translator(messages.renewalSurveyForm)
            : translator(messages.renewalSurveyFormWithPolicy, { policyNumber: _.get(renewalPolicy, 'policyNumber') });
    }, [renewalPolicy, translator]);

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    const overrideProps = {
        pageHeaderTitle: {
            content: getPageTitleInfo()
        },
        plRenewalSurveyComponent: {
            questionSet: plQuestionSet,
            handleValueChange: handlePLValueChange,
            stateList: stateList
        },
        eplRenewalSurveyComponent: {
            questionSet: eplQuestionSet,
            handleValueChange: handleEPLValueChange
        },
        plAcknowledgement: {
            value: plAcknowledged,
            onValueChange: handlePLAgreeChange
        },
        eplAcknowledgement: {
            value: eplAcknowledged,
            onValueChange: handleEPLAgreeChange
        },
        submitButton: {
            disabled: isFormValid()
        },
        plRenewalSurveyComponentCard: {
            header: translator(messages.plSurvey),
            errorState: plAcknowledged && !plFormValid
        },
        eplRenewalSurveyComponentCard: {
            header: translator(messages.eplSurvey),
            visible: _.get(renewalPolicy, 'isEPLRenewalSurveyAvailable', false),
            errorState: eplAcknowledged && !eplFormValid
        },
        confirmationInfo: {
            value: getConfirmationInfo()
        },
        surveyContainer: {
            visible: !formSubmitted && !_.isEmpty(renewalPolicy)
        },
        confirmationContainer: {
            visible: formSubmitted || _.isEmpty(renewalPolicy)
        },
        plRenewalSurveyAccordion: {
            chevronAlignment: 'left'
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            surveyquestionparser: SurveyQuestionParser
        },
        resolveCallbackMap: {
            handleSubmitClick,
            handleCloseClick
        }
    };

    return renderContentFromMetadata(metadata.pageContent, overrideProps, resolvers);
}

export default withRouter(withApplicationPropertyContext(PLRenewalSurvey));
